<template>
  <div>
    <div class="sw-wrap mb-3">
      <div class="gradient-line-wrapper"></div>
      <b-card class="sw-inner-card border-0">
        <div class="sw-form-header mb-2">
          <div class="d-flex align-items-start flex-wrap" style="gap: 0.75rem">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="sw-back-btn"
              v-b-tooltip.hover.top="'Back to Inbox'"
              @click="onBack"
            >
              <feather-icon icon="ArrowLeftIcon" size="16" />
            </b-button>
            <div>
              <h4 class="sw-form-title mb-25">
                {{ form.id ? "Edit Withdrawal" : "New Withdrawal" }}
              </h4>
              <p class="sw-form-subtitle mb-0">
                {{
                  parentInitiate
                    ? "Review parent request, complete details, and proceed to Branch Head"
                    : "Fill details and proceed to Branch Head"
                }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-4">
          <b-spinner variant="primary" />
        </div>

        <b-form v-else @submit.prevent>
          <!-- Student search -->
          <b-form-group label="Student *" class="mb-2">
            <template v-if="!form.id">
              <div v-if="!selectedStudent" class="search-section">
                <b-form-input
                  v-model="studentSearchQuery"
                  placeholder="Search by name or GR..."
                  class="search-input"
                  @input="onStudentSearchChange"
                />

                <div
                  v-if="studentSearchQuery.trim().length >= 2"
                  class="search-results"
                >
                  <div v-if="studentSearchLoading" class="loading-state">
                    <b-spinner small variant="primary" class="mr-50" />
                    Searching...
                  </div>

                  <div
                    v-else-if="filteredStudentOptions.length"
                    class="student-options"
                  >
                    <div
                      v-for="student in filteredStudentOptions"
                      :key="student.id"
                      class="student-option"
                      @click="selectStudent(student)"
                    >
                      <div class="d-flex align-items-center">
                        <b-avatar
                          :src="student.picture || ''"
                          size="36"
                          variant="light-primary"
                          class="mr-1"
                        />
                        <div>
                          <strong>{{ student.name }}</strong>
                          <div class="text-muted small">
                            <span v-if="student.grNo"
                              >GR {{ student.grNo }}</span
                            >
                            <span v-if="student.className">
                              · {{ student.className
                              }}{{
                                student.sectionName
                                  ? " - " + student.sectionName
                                  : ""
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="empty-state text-muted small py-1 px-1">
                    No students found
                  </div>
                </div>
              </div>

              <div v-else class="selected-student-card">
                <div class="d-flex align-items-start">
                  <b-avatar
                    :src="selectedStudent.picture || ''"
                    size="52"
                    variant="light-primary"
                    class="mr-1 flex-shrink-0"
                  />
                  <div class="flex-grow-1 min-width-0">
                    <div
                      class="d-flex justify-content-between align-items-start"
                    >
                      <div>
                        <h6 class="mb-0 sw-student-name">
                          {{ selectedStudent.name }}
                        </h6>
                        <p
                          v-if="selectedStudent.father_name"
                          class="mb-50 text-muted small"
                        >
                          F/N: {{ selectedStudent.father_name }}
                        </p>
                      </div>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="btn-icon"
                        v-b-tooltip.hover.top="'Clear student'"
                        @click="clearSelectedStudent"
                      >
                        <feather-icon icon="XIcon" size="14" />
                      </b-button>
                    </div>
                    <div class="d-flex flex-wrap align-items-center mb-50" style="gap: 0.35rem">
                      <b-badge
                        v-if="selectedStudent.grNo"
                        variant="light-primary"
                      >
                        GR {{ selectedStudent.grNo }}
                      </b-badge>
                      <b-badge
                        v-if="selectedStudentClassLabel"
                        variant="light-info"
                      >
                        {{ selectedStudentClassLabel }}
                      </b-badge>
                    </div>
                    <div
                      v-if="form.parentContact"
                      class="sw-info-pill"
                    >
                      <feather-icon
                        icon="PhoneIcon"
                        size="14"
                        class="mr-50 text-primary"
                      />
                      <span>{{ form.parentContact }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="selected-student-card">
              <strong>{{ selectedStudentLabel || "Student" }}</strong>
              <div v-if="parentInitiate" class="text-center mt-50">
                <b-badge variant="light-warning">Parent initiated</b-badge>
              </div>
            </div>
          </b-form-group>

          <b-row>
            <b-col md="6">
              <b-form-group label="Last attendance date">
                <flat-pickr
                  v-model="form.lastAttendanceDate"
                  class="form-control"
                  :config="dateConfig"
                  placeholder="Select date"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Leaving category">
                <v-select
                  v-model="form.leavingCategory"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="[{ title: 'None' }, ...leftOptions]"
                  :reduce="(opt) => opt.title"
                  label="title"
                  :clearable="false"
                  placeholder="Select category"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Reason *">
                <b-form-textarea
                  v-model="form.reason"
                  rows="2"
                  placeholder="Why is the student leaving?"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Parent contact">
                <b-form-input
                  v-model="form.parentContact"
                  placeholder="Phone / mobile"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Forwarding school">
                <b-form-input
                  v-model="form.forwardingSchool"
                  placeholder="School name"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="TC required">
                <v-select
                  v-model="form.tcRequired"
                  :options="tcOptions"
                  :reduce="(o) => o.value"
                  label="text"
                  :clearable="false"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Remarks">
                <b-form-textarea v-model="form.remarks" rows="2" />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex flex-wrap justify-content-end" style="gap: 0.5rem">
            <b-button
              variant="outline-secondary"
              :disabled="saving"
              @click="onBack"
            >
              Cancel
            </b-button>
            <b-button
              variant="outline-primary"
              :disabled="saving"
              @click="save(false)"
            >
              <b-spinner v-if="saving && !submitting" small class="mr-50" />
              Save draft
            </b-button>
            <b-button
              variant="gradient-primary"
              :disabled="saving"
              @click="save(true)"
            >
              <b-spinner v-if="submitting" small class="mr-50" />
              Proceed to Branch Head
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BSpinner,
  BAvatar,
  BBadge,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import {
  withdrawalMixin,
  isParentInitiatedWithdrawal,
} from "./withdrawalShared";

export default {
  name: "WithdrawalForm",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BSpinner,
    BAvatar,
    BBadge,
    vSelect,
    flatPickr,
  },
  mixins: [withdrawalMixin],
  props: {
    embedded: { type: Boolean, default: false },
    editId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      submitting: false,
      studentSearchQuery: "",
      studentSearchLoading: false,
      filteredStudentOptions: [],
      studentSearchCache: {},
      studentSearchTimer: null,
      lastStudentSearchToken: 0,
      selectedStudent: null,
      selectedStudentLabel: "",
      parentInitiate: false,
      leftOptions: [],
      tcOptions: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      form: {
        id: 0,
        sID: null,
        classID: null,
        sectionID: null,
        lastAttendanceDate: "",
        leavingCategory: "None",
        reason: "",
        parentContact: "",
        forwardingSchool: "",
        tcRequired: true,
        remarks: "",
      },
    };
  },
  async created() {
    try {
      await this.loadRoleSetup();
    } catch (e) {
      this.toastErr("Failed to load role setup");
      this.onBack();
      return;
    }
    if (!this.canCreateWithdrawal) {
      this.toastErr("Only Admission Officer can create/edit withdrawal forms");
      this.onBack();
      return;
    }
    await this.loadLeftCategories();
    const id = this.editId || this.$route.query.id;
    if (id) this.loadExisting(id);
  },
  beforeDestroy() {
    if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
  },
  watch: {
    editId(val) {
      if (val) this.loadExisting(val);
    },
  },
  computed: {
    selectedStudentClassLabel() {
      const s = this.selectedStudent;
      if (!s) return "";
      if (s.className) {
        return s.sectionName ? `${s.className} - ${s.sectionName}` : s.className;
      }
      if (s.meta_class) return s.meta_class;
      return "";
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),
    onBack() {
      if (this.embedded) this.$emit("back");
      else this.$router.push({ name: "student-withdrawal" });
    },
    /** Prefer positive IDs — class_current can be 0 while class_admitted is set */
    pickPositiveId(...values) {
      for (const v of values) {
        const n = Number(v);
        if (!Number.isNaN(n) && n > 0) return n;
      }
      return null;
    },
    pickClassID(student) {
      if (!student) return null;
      const sections = student.sections || {};
      return this.pickPositiveId(
        student.classID,
        student.classId,
        student.class_current,
        student.class_admitted,
        sections.classID,
        sections.classId,
        student.cls_current && student.cls_current.id,
        student.cls_admitted && student.cls_admitted.id
      );
    },
    pickSectionID(student) {
      if (!student) return null;
      const sections = student.sections || {};
      return this.pickPositiveId(
        student.sectionID,
        student.sectionId,
        student.section_current,
        student.section_admitted,
        sections.id,
        sections.sectionID
      );
    },
    pickParentContact(student) {
      if (!student) return "";
      const synced = student.syncedDetails || {};
      const candidates = [
        student.contact_father,
        student.contact_mother,
        student.contact_student,
        student.contact_home,
        student.contact_tutor,
        student.emergency_contact,
        student.office_phone,
        student.mother_office_phone,
        student.guardian_office_phone,
        student.fatherMobile,
        student.fatherContact,
        student.motherMobile,
        student.motherContact,
        student.parentContact,
        student.contact,
        student.mobile,
        student.phone,
        student.guardianContact,
        synced.father && synced.father.number,
        synced.mother && synced.mother.number,
        synced.student && synced.student.number,
      ];
      for (const c of candidates) {
        const val = String(c || "").trim();
        if (val) return val;
      }
      return "";
    },
    applyStudentIdsAndContact(student) {
      if (!student) return;
      const classID = this.pickClassID(student);
      const sectionID = this.pickSectionID(student);
      if (classID) this.form.classID = classID;
      if (sectionID) this.form.sectionID = sectionID;
      const contact = this.pickParentContact(student);
      if (contact) this.form.parentContact = contact;
    },
    onStudentSearchChange() {
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700);
    },
    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim().toLowerCase();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        return;
      }
      if (this.studentSearchCache[term]) {
        this.filteredStudentOptions = this.studentSearchCache[term];
        return;
      }

      const token = ++this.lastStudentSearchToken;
      this.studentSearchLoading = true;
      try {
        const response = await this.get({
          url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(this.studentSearchQuery)}&type=`,
          token: this.$store.state.userData.token,
        });
        if (token !== this.lastStudentSearchToken) return;

        let studentsData = [];
        if (response && response.data && Array.isArray(response.data)) {
          studentsData = response.data;
        } else if (Array.isArray(response)) {
          studentsData = response;
        }

        const mapped = studentsData.map((student) => {
          let className = "";
          let sectionName = "";
          if (student.meta_class) {
            const parts = String(student.meta_class).split(" - ");
            className = (parts[0] || "").trim();
            sectionName = (parts[1] || "").trim();
          } else {
            className = student.className || student.cls || "";
            sectionName = student.sectionName || student.section || "";
          }
          return {
            ...student,
            id: student.id || student.sID,
            name: student.name || "Unknown Student",
            className,
            sectionName,
            grNo: student.grNo || student.gr || "",
            classID: this.pickClassID(student),
            sectionID: this.pickSectionID(student),
          };
        });

        this.filteredStudentOptions = mapped;
        this.studentSearchCache[term] = mapped;
      } catch (e) {
        console.error("Student search error:", e);
        this.filteredStudentOptions = [];
      } finally {
        if (token === this.lastStudentSearchToken) {
          this.studentSearchLoading = false;
        }
      }
    },
    async loadLeftCategories() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "DropDownList/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&category=leftCategories",
          token: this.$store.state.userData.token,
        });
        this.leftOptions = Array.isArray(res) ? res : (res && res.data) || [];
      } catch (e) {
        console.error("Failed to load leaving categories", e);
        this.leftOptions = [];
      }
    },
    async fillContactFromStudent(sID) {
      if (!sID) return;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Students/LoadStudentData?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            sID,
          token: this.$store.state.userData.token,
        });
        const st = (res && res.student) || res || {};

        // Merge full record into selected card display
        this.selectedStudent = {
          ...(this.selectedStudent || {}),
          ...st,
          id: st.id || sID,
          name: st.name || (this.selectedStudent && this.selectedStudent.name),
          grNo:
            st.grNo ||
            (this.selectedStudent && this.selectedStudent.grNo) ||
            "",
          father_name: st.father_name || "",
          picture: st.picture || "",
          className:
            (st.meta_class && String(st.meta_class).split(" - ")[0]) ||
            (this.selectedStudent && this.selectedStudent.className) ||
            "",
          sectionName:
            (st.meta_class && String(st.meta_class).split(" - ")[1]) ||
            (this.selectedStudent && this.selectedStudent.sectionName) ||
            "",
        };

        this.applyStudentIdsAndContact(st);
      } catch (e) {
        console.error("Failed to load student contact", e);
      }
    },
    async selectStudent(student) {
      this.selectedStudent = student;
      this.form.sID = student.id || student.sID;
      this.form.classID = this.pickClassID(student);
      this.form.sectionID = this.pickSectionID(student);
      this.form.parentContact = this.pickParentContact(student) || "";

      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.selectedStudentLabel = `${student.name} (GR ${
        student.grNo || "—"
      })`;

      await this.fillContactFromStudent(this.form.sID);
    },
    clearSelectedStudent() {
      this.selectedStudent = null;
      this.selectedStudentLabel = "";
      this.form.sID = null;
      this.form.classID = null;
      this.form.sectionID = null;
      this.form.parentContact = "";
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },
    async loadExisting(id) {
      this.loading = true;
      try {
        const res = await this.withdrawalGet("LoadSelected", { id });
        const detail = this.extractDetail(res);
        if (!detail) return;
        const h = detail.header || {};
        const st = detail.student || {};
        this.parentInitiate = isParentInitiatedWithdrawal(detail);

        const canEditForm = detail.permissions?.canEditForm;
        const allowParentEdit =
          this.parentInitiate &&
          String(h.status || "") === "pending_admission" &&
          this.hasWithdrawalRole("admission_officer");

        if (canEditForm === false && !allowParentEdit) {
          if (this.embedded) this.$emit("saved", id);
          else {
            this.$router.replace({
              name: "student-withdrawal-detail",
              params: { id: String(id) },
            });
          }
          return;
        }
        this.form = {
          id: h.id || Number(id),
          sID: h.sID || st.id,
          classID: h.classID || st.classID,
          sectionID: h.sectionID || st.sectionID,
          lastAttendanceDate: h.lastAttendanceDate
            ? String(h.lastAttendanceDate).slice(0, 10)
            : "",
          leavingCategory: h.leavingCategory || "None",
          reason: h.reason || "",
          parentContact: h.parentContact || "",
          forwardingSchool: h.forwardingSchool || "",
          tcRequired: h.tcRequired !== false,
          remarks: h.remarks || "",
        };
        this.selectedStudent = {
          id: st.id || h.sID,
          name: st.name || "Student",
          grNo: st.grNo || "",
          className: st.className || "",
          sectionName: st.sectionName || "",
        };
        this.selectedStudentLabel = `${st.name || "Student"} (GR ${
          st.grNo || "—"
        })`;
      } catch (e) {
        console.error(e);
        this.toastErr("Failed to load withdrawal");
      } finally {
        this.loading = false;
      }
    },
    validate(forSubmit) {
      if (!this.form.sID) {
        this.toastErr("Please select a student");
        return false;
      }
      if (forSubmit) {
        if (!(this.form.reason || "").trim()) {
          this.toastErr("Reason is required before submit");
          return false;
        }
      }
      return true;
    },
    /** API often returns "Sentence one. Sentence two." — show only the first sentence. */
    formatWithdrawalError(msg) {
      const text = String(msg || "").trim();
      if (!text) return "Save failed";
      const first = text.split(/\.\s+/)[0].trim();
      if (!first) return text;
      return /[.!?]$/.test(first) ? first : `${first}.`;
    },
    async save(submit) {
      if (!this.validate(submit)) return;
      this.saving = true;
      this.submitting = submit;
      try {
        const body = {
          ...this.form,
          id: this.form.id || 0,
          lastAttendanceDate: this.form.lastAttendanceDate
            ? String(this.form.lastAttendanceDate).slice(0, 10)
            : null,
          leavingCategory:
            this.form.leavingCategory === "None"
              ? ""
              : this.form.leavingCategory,
        };
        const res = await this.withdrawalPost(
          "Save",
          body,
          {
            userID: this.withdrawalUserID,
            submit: submit ? "true" : "false",
          },
          {
            showError: true,
            errorFirstSentence: true,
          }
        );

        // API status:"error" → toast already shown (first sentence only)
        if (res === false) {
          return;
        }

        const apiError =
          (res && res.status === "error" && (res.data || res.message)) ||
          (res &&
            res.data &&
            res.data.status === "error" &&
            (res.data.data || res.data.message));

        if (apiError) {
          this.toastErr(this.formatWithdrawalError(apiError));
          return;
        }

        const msg = String(
          (res && (res.message || res.Message)) ||
            (res && res.data && res.data.message) ||
            (typeof res === "string" ? res : "") ||
            ""
        ).toLowerCase();

        if (msg.includes("open withdrawal already exists")) {
          const existingId =
            (res && res.data && (res.data.id || res.data.existingId)) ||
            (res && res.id);
          this.toastErr("Open withdrawal already exists for this student");
          if (existingId) {
            if (this.embedded) this.$emit("saved", existingId);
            else {
              this.$router.push({
                name: "student-withdrawal-detail",
                params: { id: String(existingId) },
              });
            }
          }
          return;
        }

        let id =
          this.form.id ||
          (res &&
            res.data &&
            (res.data.id ||
              (res.data.header && res.data.header.id) ||
              res.data)) ||
          (res && res.id);

        if (submit && id) {
          const status =
            (res && res.data && res.data.status) ||
            (res && res.data && res.data.header && res.data.header.status);
          if (status === "draft" || !status) {
            await this.withdrawalPost(
              "Submit",
              {},
              { id, userID: this.withdrawalUserID }
            );
          }
        }

        this.toastOk(submit ? "Proceeded to Branch Head" : "Draft saved");
        if (this.embedded) this.$emit("saved", id || null);
        else if (id) {
          this.$router.push({
            name: "student-withdrawal-detail",
            params: { id: String(id) },
          });
        } else {
          this.onBack();
        }
      } catch (e) {
        console.error(e);
        const errMsg =
          (e &&
            e.response &&
            e.response.data &&
            (e.response.data.data || e.response.data.message)) ||
          (e && e.message) ||
          "Save failed";
        this.toastErr(this.formatWithdrawalError(errMsg));
      } finally {
        this.saving = false;
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
@import "./withdrawalPanelStyles.scss";

.sw-wrap {
  overflow: visible;
  padding-top: 1.5rem;
}

.sw-form-header {
  padding-top: 0.25rem;
  margin-bottom: 1rem;
}

.sw-form-title {
  color: #2d1b69;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.4;
  letter-spacing: 0;
  margin: 0;
}

.sw-form-subtitle {
  color: #6e6b7b;
  font-size: 0.875rem;
  line-height: 1.4;
}

.search-section {
  position: relative;
}

.search-input {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  min-height: 44px;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.search-results {
  margin-top: 0.5rem;
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  background: #fff;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.1);
}

.student-option {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0edff;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f7ff;
  }
}

.loading-state,
.empty-state {
  padding: 0.85rem 1rem;
}

.selected-student-card {
  border: 1.5px solid #e0d8ff;
  background: #f8f7ff;
  border-radius: 14px;
  padding: 0.9rem 1rem;
}

.sw-student-name {
  color: #2d1b69;
  font-weight: 700;
}

.sw-info-pill {
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.8rem;
  color: #4a4568;
  margin-top: 0.25rem;
}

.btn-icon {
  min-width: 32px;
  padding: 0.25rem 0.45rem;
  border-radius: 8px;
}
</style>
